import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  OrganizationContext,
  type Organization,
} from "@/contexts/OrganizationContext";
import { OrgSettingsContext } from "@/contexts/OrgSettingsContext";
import type {
  StorefrontSectionRow,
  StorefrontThemeConfig,
} from "@/modules/ecommerce/services/storefront-web-service";
import { buildStorefrontOrgSettingsValue } from "../lib/storefrontOrgSettings";

/**
 * Shape of the row returned by the `get_org_by_host` RPC. Only safe,
 * world-readable columns — never the gate password hash.
 *
 * The currency / locale fields are surfaced explicitly so the public
 * storefront can format prices without granting `anon` SELECT on
 * `organization_settings`.
 */
export interface HostOrgRow {
  organization_id: string;
  organization_name: string;
  storefront_online: boolean | null;
  storefront_maintenance: boolean | null;
  storefront_maintenance_message: string | null;
  storefront_password_enabled: boolean | null;
  storefront_theme: StorefrontThemeConfig | null;
  storefront_sections: StorefrontSectionRow[] | null;
  storefront_allow_search_indexing: boolean | null;
  store_name: string | null;
  currency_code: string | null;
  number_locale: string | null;
}

export type HostOrgStatus =
  | { kind: "loading" }
  | { kind: "not-found"; host: string }
  | { kind: "error"; message: string }
  | { kind: "ok"; row: HostOrgRow };

interface HostOrgContextValue {
  status: HostOrgStatus;
  reload: () => void;
}

const HostOrgContext = createContext<HostOrgContextValue | undefined>(
  undefined,
);

// Bump SCHEMA_VERSION whenever the `HostOrgRow` shape changes in a
// non-backward-compatible way (added required field, renamed field, etc.).
// The cache reader compares the stored value against this constant and
// silently discards stale shapes — old tabs keep working, they just take
// the cost of one extra RPC round-trip after a deploy.
const CACHE_KEY = "sbms_storefront_host_org";
const SCHEMA_VERSION = 2;

interface CachedEnvelope {
  schemaVersion: number;
  host: string;
  row: HostOrgRow;
}

function readCachedRow(host: string): HostOrgRow | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedEnvelope> | null;
    if (!parsed || typeof parsed !== "object") return null;
    if (parsed.schemaVersion !== SCHEMA_VERSION) return null;
    if (parsed.host !== host) return null;
    if (!parsed.row || !parsed.row.organization_id) return null;
    return parsed.row;
  } catch {
    return null;
  }
}

function writeCachedRow(host: string, row: HostOrgRow) {
  try {
    const envelope: CachedEnvelope = {
      schemaVersion: SCHEMA_VERSION,
      host,
      row,
    };
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(envelope));
  } catch {
    /* ignore — private window or storage disabled */
  }
}

export function HostOrganizationProvider({ children }: { children: ReactNode }) {
  const host =
    typeof window === "undefined" ? "" : window.location.host.toLowerCase();

  const [status, setStatus] = useState<HostOrgStatus>(() => {
    const cached = host ? readCachedRow(host) : null;
    return cached ? { kind: "ok", row: cached } : { kind: "loading" };
  });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!host) {
      setStatus({ kind: "not-found", host: "" });
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.rpc("get_org_by_host", {
        p_host: host,
      });
      if (cancelled) return;
      if (error) {
        setStatus({ kind: "error", message: error.message });
        return;
      }
      // The generator types `storefront_theme` / `storefront_sections` as
      // `Json` because they are jsonb on the server; cast to the local
      // `HostOrgRow` so the rest of the provider stays strongly typed.
      const row = (data?.[0] as HostOrgRow | undefined) ?? null;
      if (!row || !row.organization_id) {
        setStatus({ kind: "not-found", host });
        return;
      }
      writeCachedRow(host, row);
      setStatus({ kind: "ok", row });
    })();
    return () => {
      cancelled = true;
    };
  }, [host, reloadToken]);

  const orgValue = useMemo(() => {
    if (status.kind !== "ok") return undefined;
    const synthOrganization: Organization = {
      id: status.row.organization_id,
      name: status.row.organization_name,
      domain: null,
      status: "active",
      settings: {},
      created_at: new Date(0).toISOString(),
    };
    return {
      currentOrganization: synthOrganization,
      setCurrentOrganization: () => {
        /* no-op in public storefront */
      },
      userOrganizations: [synthOrganization],
      isLoading: false,
      error: null,
      refreshOrganizations: async () => {
        setReloadToken((n) => n + 1);
      },
      userRole: null,
      isAdmin: false,
    };
  }, [status]);

  // Always provide a valid OrgSettings value, even before the host RPC
  // resolves. This keeps `useOrgSettingsCtx` callable from any descendant
  // — including ones that mount during the loading state — without
  // tripping the "must be used inside provider" guard. Once the row
  // arrives, the synthetic value picks up the tenant's currency / locale.
  const orgSettingsValue = useMemo(() => {
    if (status.kind !== "ok") {
      return buildStorefrontOrgSettingsValue({
        currencyCode: null,
        numberLocale: null,
      });
    }
    return buildStorefrontOrgSettingsValue({
      currencyCode: status.row.currency_code,
      numberLocale: status.row.number_locale,
    });
  }, [status]);

  const ctxValue = useMemo<HostOrgContextValue>(
    () => ({ status, reload: () => setReloadToken((n) => n + 1) }),
    [status],
  );

  // Status screens are rendered up in <App>, but the context tree below
  // always provides org + settings values when status is "ok".
  return (
    <HostOrgContext.Provider value={ctxValue}>
      <OrganizationContext.Provider value={orgValue}>
        <OrgSettingsContext.Provider value={orgSettingsValue}>
          {children}
        </OrgSettingsContext.Provider>
      </OrganizationContext.Provider>
    </HostOrgContext.Provider>
  );
}

export function useHostOrg(): HostOrgContextValue {
  const v = useContext(HostOrgContext);
  if (!v) throw new Error("useHostOrg must be used inside HostOrganizationProvider");
  return v;
}
